import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';

const COLORS = {
  start: '#1976d2',
  condition: '#fff3e0',
  conditionBorder: '#e65100',
  action: '#e8f5e9',
  actionBorder: '#2e7d32',
  fallback: '#f0fff0',
  fallbackBorder: '#66bb6a',
  surcharge: '#fff3e0',
  surchargeBorder: '#e65100',
  end: '#616161',
  arrow: '#666',
  yesArrow: '#2e7d32',
  noArrow: '#d32f2f',
  text: '#333',
  labelText: '#fff',
};

const NODE_W = 280;
const NODE_H = 48;
const DIAMOND_W = 300;
const DIAMOND_H = 70;
const ACTION_W = 220;
const ACTION_H = 44;
const GAP_Y = 30;
const ACTION_GAP_X = 80;

function getConditionText(cond) {
  if (!cond.conditions || cond.conditions.length === 0) return 'Always True';
  return cond.conditions
    .map(c => `${shortAttr(c.attr)} ${c.op} ${c.value}`)
    .join(' AND ');
}

function shortAttr(attr) {
  if (!attr) return '?';
  const parts = attr.split('.');
  return parts[parts.length - 1];
}

function getActionText(cond, priceTables) {
  switch (cond.actionType) {
    case 'table': {
      const table = priceTables.find(t => t.id === cond.tableId);
      return `Table: ${table?.name || table?.code || cond.tableId || '?'}`;
    }
    case 'formula':
      return `Formula: ${cond.formula || '?'} x $${cond.unitPrice || '?'}`;
    case 'surcharge':
      return `Surcharge: $${cond.surchargeAmount || '?'} (${cond.surchargeType || 'fixed'})`;
    default:
      return 'No action';
  }
}

function truncateText(text, maxLen) {
  return text.length > maxLen ? text.substring(0, maxLen - 2) + '..' : text;
}

function getSurchargeText(surcharge) {
  if (!surcharge) return '';
  const type = surcharge.type || 'fixed';
  if (type === 'fixed') return `Fixed: $${surcharge.value || 0}`;
  if (type === '1d') return `1D Lookup (key: ${shortAttr(surcharge.key1Attr)})`;
  if (type === '2d') return `2D Lookup (${shortAttr(surcharge.key1Attr)}, ${shortAttr(surcharge.key2Attr)})`;
  return type;
}

const SURCHARGE_W = 260;
const SURCHARGE_H = 52;

const PricingRuleDiagram = ({ open, onClose, conditions, ruleName, priceTables, surcharge }) => {
  if (!open) return null;

  const items = conditions || [];
  const centerX = (DIAMOND_W / 2) + 40;
  const actionX = centerX + (DIAMOND_W / 2) + ACTION_GAP_X;

  // Calculate positions
  let y = 20;
  const nodes = [];

  // Start node
  nodes.push({ type: 'start', x: centerX, y, label: ruleName || 'Rule' });
  y += NODE_H + GAP_Y;

  // Condition/action pairs
  items.forEach((cond, i) => {
    const isAlwaysTrue = !cond.conditions || cond.conditions.length === 0;
    const condText = getConditionText(cond);
    const actionText = getActionText(cond, priceTables || []);
    const descText = cond.description || '';

    nodes.push({
      type: isAlwaysTrue ? 'fallback' : 'condition',
      x: centerX,
      y,
      label: condText,
      index: i + 1,
      actionText,
      actionX,
      descText,
    });
    y += DIAMOND_H + GAP_Y;
  });

  // Surcharge node (optional)
  if (surcharge) {
    nodes.push({
      type: 'surcharge',
      x: centerX,
      y,
      label: getSurchargeText(surcharge),
      condition: surcharge.condition || '',
    });
    y += SURCHARGE_H + GAP_Y;
  }

  // End node
  nodes.push({ type: 'end', x: centerX, y });
  y += NODE_H + 20;

  const svgWidth = actionX + ACTION_W + 40;
  const svgHeight = y;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Rule Diagram: {ruleName}</DialogTitle>
      <DialogContent>
        <Box sx={{ overflow: 'auto', textAlign: 'center' }}>
          <svg width={svgWidth} height={svgHeight} style={{ minWidth: svgWidth }}>
            <defs>
              <marker id="arrowDown" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 Z" fill={COLORS.arrow} />
              </marker>
              <marker id="arrowRight" markerWidth="8" markerHeight="8" refX="8" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 Z" fill={COLORS.yesArrow} />
              </marker>
            </defs>

            {nodes.map((node, i) => {
              if (node.type === 'start') {
                return (
                  <g key={i}>
                    <rect
                      x={node.x - NODE_W / 2} y={node.y}
                      width={NODE_W} height={NODE_H}
                      rx={NODE_H / 2} ry={NODE_H / 2}
                      fill={COLORS.start}
                    />
                    <text
                      x={node.x} y={node.y + NODE_H / 2 + 5}
                      textAnchor="middle" fill={COLORS.labelText}
                      fontSize="13" fontWeight="bold"
                    >
                      {truncateText(node.label, 35)}
                    </text>
                    {/* Arrow down to first condition */}
                    {nodes[i + 1] && (
                      <line
                        x1={node.x} y1={node.y + NODE_H}
                        x2={node.x} y2={nodes[i + 1].y}
                        stroke={COLORS.arrow} strokeWidth={2}
                        markerEnd="url(#arrowDown)"
                      />
                    )}
                  </g>
                );
              }

              if (node.type === 'condition' || node.type === 'fallback') {
                const isFallback = node.type === 'fallback';
                const dw = DIAMOND_W / 2;
                const dh = DIAMOND_H / 2;
                const cx = node.x;
                const cy = node.y + dh;

                // Diamond points
                const points = `${cx},${node.y} ${cx + dw},${cy} ${cx},${node.y + DIAMOND_H} ${cx - dw},${cy}`;

                const nextNode = nodes[i + 1];
                const actionCenterX = node.actionX + ACTION_W / 2;
                const actionCenterY = node.y + (DIAMOND_H - ACTION_H) / 2;

                return (
                  <g key={i}>
                    {/* Priority badge */}
                    <circle cx={cx - dw + 5} cy={node.y + 5} r={10} fill={COLORS.start} />
                    <text x={cx - dw + 5} y={node.y + 9} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">
                      {node.index}
                    </text>

                    {/* Diamond */}
                    <polygon
                      points={points}
                      fill={isFallback ? COLORS.fallback : COLORS.condition}
                      stroke={isFallback ? COLORS.fallbackBorder : COLORS.conditionBorder}
                      strokeWidth={2}
                    />
                    <text
                      x={cx} y={cy + 4}
                      textAnchor="middle" fill={COLORS.text}
                      fontSize="11" fontWeight="500"
                    >
                      {truncateText(node.label, 38)}
                    </text>

                    {/* YES arrow to action box */}
                    <line
                      x1={cx + dw} y1={cy}
                      x2={node.actionX} y2={cy}
                      stroke={COLORS.yesArrow} strokeWidth={2}
                      markerEnd="url(#arrowRight)"
                    />
                    <text
                      x={cx + dw + 8} y={cy - 6}
                      fill={COLORS.yesArrow} fontSize="10" fontWeight="bold"
                    >
                      {isFallback ? '' : 'YES'}
                    </text>

                    {/* Action box */}
                    <rect
                      x={node.actionX} y={actionCenterY}
                      width={ACTION_W} height={ACTION_H}
                      rx={6} ry={6}
                      fill={COLORS.action}
                      stroke={COLORS.actionBorder}
                      strokeWidth={1.5}
                    />
                    <text
                      x={node.actionX + ACTION_W / 2}
                      y={actionCenterY + (node.descText ? 17 : 22)}
                      textAnchor="middle" fill={COLORS.text}
                      fontSize="11" fontWeight="600"
                    >
                      {truncateText(node.actionText, 30)}
                    </text>
                    {node.descText && (
                      <text
                        x={node.actionX + ACTION_W / 2}
                        y={actionCenterY + 33}
                        textAnchor="middle" fill="#888"
                        fontSize="9"
                      >
                        {truncateText(node.descText, 35)}
                      </text>
                    )}

                    {/* NO arrow down to next node */}
                    {nextNode && !isFallback && (
                      <>
                        <line
                          x1={cx} y1={node.y + DIAMOND_H}
                          x2={cx} y2={nextNode.y || nextNode.y}
                          stroke={COLORS.noArrow} strokeWidth={2}
                          markerEnd="url(#arrowDown)"
                        />
                        <text
                          x={cx + 8} y={node.y + DIAMOND_H + 15}
                          fill={COLORS.noArrow} fontSize="10" fontWeight="bold"
                        >
                          NO
                        </text>
                      </>
                    )}

                    {/* Fallback arrow down to end */}
                    {nextNode && isFallback && (
                      <line
                        x1={cx} y1={node.y + DIAMOND_H}
                        x2={cx} y2={nextNode.y}
                        stroke={COLORS.arrow} strokeWidth={2}
                        markerEnd="url(#arrowDown)"
                      />
                    )}
                  </g>
                );
              }

              if (node.type === 'surcharge') {
                const nextNode = nodes[i + 1];
                const bw = SURCHARGE_W;
                const bh = SURCHARGE_H;
                return (
                  <g key={i}>
                    <rect
                      x={node.x - bw / 2} y={node.y}
                      width={bw} height={bh}
                      rx={6} ry={6}
                      fill={COLORS.surcharge}
                      stroke={COLORS.surchargeBorder}
                      strokeWidth={2}
                      strokeDasharray="6,3"
                    />
                    <text
                      x={node.x} y={node.y + (node.condition ? 18 : bh / 2 + 4)}
                      textAnchor="middle" fill={COLORS.surchargeBorder}
                      fontSize="10" fontWeight="bold"
                    >
                      SURCHARGE (optional)
                    </text>
                    <text
                      x={node.x} y={node.y + (node.condition ? 34 : bh / 2 + 4) }
                      textAnchor="middle" fill={COLORS.text}
                      fontSize="11" fontWeight="600"
                    >
                      {truncateText(node.label, 35)}
                    </text>
                    {node.condition && (
                      <text
                        x={node.x} y={node.y + 47}
                        textAnchor="middle" fill="#888"
                        fontSize="9"
                      >
                        when: {truncateText(node.condition, 30)}
                      </text>
                    )}
                    {nextNode && (
                      <line
                        x1={node.x} y1={node.y + bh}
                        x2={node.x} y2={nextNode.y}
                        stroke={COLORS.arrow} strokeWidth={2}
                        markerEnd="url(#arrowDown)"
                      />
                    )}
                  </g>
                );
              }

              if (node.type === 'end') {
                return (
                  <g key={i}>
                    <rect
                      x={node.x - 40} y={node.y}
                      width={80} height={32}
                      rx={16} ry={16}
                      fill={COLORS.end}
                    />
                    <text
                      x={node.x} y={node.y + 20}
                      textAnchor="middle" fill={COLORS.labelText}
                      fontSize="12" fontWeight="bold"
                    >
                      END
                    </text>
                  </g>
                );
              }

              return null;
            })}
          </svg>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PricingRuleDiagram;
